import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDamageClaim, damageClaimStatusBadge, withdrawDamageClaim,
} from '../../services/disputeService';
import toast from 'react-hot-toast';

export default function DamageClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');

  const load = () => {
    setLoading(true);
    getDamageClaim(claimId)
      .then(setClaim)
      .catch((err) => toast.error(err.message || 'Could not load claim'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [claimId]);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleWithdraw = async () => {
    setActionInProgress(true);
    try {
      await withdrawDamageClaim({ claimId, reason: withdrawReason || null });
      toast.success('Claim withdrawn.');
      setShowWithdraw(false);
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!claim) return <div className="p-10 text-center text-muted">Claim not found</div>;

  const badge = damageClaimStatusBadge(claim.status);
  const canWithdraw = ['submitted', 'admin_reviewing'].includes(claim.status);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver/claims')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All claims</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="font-serif m-0">{claim.referenceNumber}</h1>
          <div className="text-[0.78rem] text-muted mt-1 capitalize">
            {claim.type} claim · Submitted {fmtDate(claim.submittedAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Amount summary */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[0.78rem] text-muted font-semibold uppercase">Amount claimed</div>
            <div className="text-[1.2rem] font-bold text-brand-dark">${claim.amountClaimed?.toFixed(2)}</div>
          </div>
          {claim.approvedAmount != null && (
            <div>
              <div className="text-[0.78rem] text-muted font-semibold uppercase">Approved amount</div>
              <div className="text-[1.2rem] font-bold" style={{ color: '#059669' }}>${claim.approvedAmount.toFixed(2)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Trip details */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
          Trip details
        </div>
        <div className="text-[0.85rem] text-body">
          <div>Trip: <strong>{claim.bookingReferenceNumber || claim.bookingId?.substring(0, 8)}</strong></div>
          <div>Rider: <strong>{claim.riderName || '—'}</strong></div>
        </div>
      </div>

      {/* Description */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
          Description
        </div>
        <div className="text-[0.9rem] text-brand-dark whitespace-pre-wrap">{claim.description}</div>
      </div>

      {/* Damage photos */}
      {claim.photos?.length > 0 && (
        <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
          <div className="text-[0.78rem] text-muted font-semibold uppercase mb-2">
            Damage photos ({claim.photos.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {claim.photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Damage ${i + 1}`}
                  className="w-[100px] h-[100px] object-cover rounded-lg border border-slate-200" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Receipts */}
      {claim.receiptPhotos?.length > 0 && (
        <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
          <div className="text-[0.78rem] text-muted font-semibold uppercase mb-2">
            Receipts / invoices ({claim.receiptPhotos.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {claim.receiptPhotos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[0.85rem] text-brand-dark no-underline inline-flex items-center gap-1.5">📄 Receipt {i + 1}</a>
            ))}
          </div>
        </div>
      )}

      {/* Status-specific panels */}

      {/* State: submitted / admin_reviewing */}
      {(claim.status === 'submitted' || claim.status === 'admin_reviewing') && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          Your claim is awaiting admin review. You'll be notified once a decision is made (typically within 48 hours).
        </div>
      )}

      {/* State: awaiting_charge */}
      {claim.status === 'awaiting_charge' && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#dbeafe', border: '1px solid #bfdbfe', color: '#1e40af' }}>
          ✓ Approved for ${claim.approvedAmount?.toFixed(2)}. The rider is being charged now.
        </div>
      )}

      {/* State: charge_failed */}
      {claim.status === 'charge_failed' && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#fed7aa', border: '1px solid #fdba74', color: '#9a3412' }}>
          <strong>Charge failed</strong> — Admin is working on retrying the charge against the rider. No action needed from you.
        </div>
      )}

      {/* State: approved_paid */}
      {claim.status === 'approved_paid' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: 'rgba(45,212,168,0.1)', border: '1px solid #2dd4a8' }}>
          <div className="font-bold mb-1" style={{ color: '#059669' }}>
            ✓ Approved and paid
          </div>
          <div className="text-[0.85rem]" style={{ color: '#059669' }}>
            ${claim.approvedAmount?.toFixed(2)} has been transferred to you.
            {claim.driverPayoutAt && <> Payout on {fmtDate(claim.driverPayoutAt)}.</>}
          </div>
          {claim.adminNotes && (
            <div className="text-[0.82rem] mt-2 italic" style={{ color: '#059669' }}>
              Admin notes: "{claim.adminNotes}"
            </div>
          )}
        </div>
      )}

      {/* State: rejected */}
      {claim.status === 'rejected' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#fee2e2', border: '1px solid #fecaca' }}>
          <div className="font-bold mb-1" style={{ color: '#991b1b' }}>
            ✗ Claim rejected
          </div>
          {claim.adminNotes && (
            <div className="text-[0.85rem] mt-1" style={{ color: '#991b1b' }}>
              Reason: {claim.adminNotes}
            </div>
          )}
        </div>
      )}

      {/* State: withdrawn */}
      {claim.status === 'withdrawn' && (
        <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 mb-4 text-[0.9rem] text-body">
          You withdrew this claim. {claim.withdrawnReason && <>Reason: "{claim.withdrawnReason}"</>}
        </div>
      )}

      {/* Withdraw button (only for submitted / admin_reviewing) */}
      {canWithdraw && (
        <>
          {!showWithdraw ? (
            <button onClick={() => setShowWithdraw(true)} className="w-full p-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Withdraw this claim</button>
          ) : (
            <div className="p-4 rounded-2xl bg-white" style={{ border: '1px solid #fecaca' }}>
              <h3 className="mt-0 mb-2" style={{ color: '#dc2626' }}>Withdraw claim?</h3>
              <p className="text-body text-[0.85rem] mb-2.5">
                You can resubmit later if needed, but only if you're still within the {48}-hour window from trip completion.
              </p>
              <input className="form-input mb-2" placeholder="Reason (optional)"
                value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
              <div className="flex gap-2">
                <button onClick={() => { setShowWithdraw(false); setWithdrawReason(''); }} className="flex-1 p-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Keep claim</button>
                <button onClick={handleWithdraw} disabled={actionInProgress} className="flex-1 p-2.5 rounded-[10px] border-0 text-white cursor-pointer font-bold"
                  style={{ background: '#dc2626' }}>Confirm withdraw</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Status history */}
      {claim.statusHistory && claim.statusHistory.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-[0.85rem] text-body font-semibold">
            Claim history ({claim.statusHistory.length} events)
          </summary>
          <div className="mt-2 p-3 rounded-[10px] bg-slate-50 text-[0.78rem] text-body">
            {claim.statusHistory.map((h, i) => (
              <div key={i} className="mb-2 pb-2" style={{ borderBottom: i < claim.statusHistory.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <div>
                  <strong>{h.from || '—'}</strong> → <strong>{h.to}</strong>
                </div>
                {h.note && <div className="italic mt-0.5">"{h.note}"</div>}
                <div className="text-muted mt-0.5">{fmtDate(h.at)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
      <BackButtonBottom to="/driver/claims" label="All claims" />
    </div>
  );
}